import React from 'react';
import { ArrowRightIcon } from '@radix-ui/react-icons';

import { dateShort2 } from '../../../assets/contants/dates-fns';
import { formatPlainDate, plainDateFromEpochDay } from '../../../core/services/plain-date';
import { isDragDisabledFor, modeForHit } from '../interaction/drag-math';
import { useBarDrag } from '../interaction/use-bar-drag';
import { durationOf, formatDuration } from '../model/duration';
import { createTimeScale } from '../model/time-scale';
import styles from './group-bar.module.scss';

import type { GroupBarRendererProps } from '../types';

// See bar.tsx's identical constant: only reached when no live chart supplies a real `scale`.
const FALLBACK_SCALE = createTimeScale('day');

/**
 * An ALIAS of the published seam, for the reason `bar.tsx`'s `BarProps` is one (I1): a parallel
 * declaration of the same fields is what let `GanttRenderers['groupBar']` fall three props behind
 * this component across Tasks 18, 20 and 22 without anything failing to compile.
 *
 * `state`: see `bar.tsx`'s doc — `GroupBar` owns `useBarDrag` the same way `Bar` does, for the
 * same Rules-of-Hooks reason, so `state.previewSpan` is only the fallback for a direct, hook-less
 * render (this file's own pre-drag unit tests), never the live value. `suffix` is
 * `renderers.groupBarSuffix`, forwarded by `row-list.tsx` as a component rather than an element so
 * nothing is constructed for a row the chart never draws — see `RowSlot`.
 */
type GroupBarProps = GroupBarRendererProps;

export const GroupBar: React.FC<GroupBarProps> = ({
  row,
  state,
  suffix: Suffix,
  scale,
  readOnly,
  onCommit,
}) => {
  // Always disabled in practice: every `Row` this component renders IS a group, and
  // `isDragDisabledFor` checks `row.isGroup` first (spec §8: "Group rows: not draggable, matching
  // today"). Calling `useBarDrag` here anyway, through the SAME eligibility function `Bar` uses,
  // rather than simply never wiring a handler, means a future change to that rule cannot leave
  // `GroupBar` still draggable by accident — there is nothing group-specific to keep in sync.
  const drag = useBarDrag({
    span: row.span,
    item: row.item,
    scale: scale ?? FALLBACK_SCALE,
    disabled: isDragDisabledFor(row, readOnly),
    onCommit,
  });

  const previewSpan = drag.previewSpan ?? state.previewSpan;
  const span = previewSpan ?? row.span;

  if (!span) return null;

  const start = formatPlainDate(plainDateFromEpochDay(span.startDay), dateShort2);
  const end = formatPlainDate(plainDateFromEpochDay(span.endDay), dateShort2);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    const rect = event.currentTarget.getBoundingClientRect();
    drag.onPointerDown(modeForHit(event.clientX, rect))(event);
  };

  return (
    <div
      data-testid="group-bar"
      className={styles['groupBar']}
      data-depth={row.depth}
      onPointerDown={handlePointerDown}
    >
      <div className={styles['left']}>
        {/* `Title` in parent-strip.tsx:126-130 — the suffix and the name are one group, set
            apart from the dates by `.left`'s wider gap. */}
        <span className={styles['title']}>
          {Suffix ? <Suffix row={row} /> : null}

          <span className={styles['caption']}>{row.item.name}</span>
        </span>

        <span className={`${styles['caption']} ${styles['dates']}`}>
          {start}
          <ArrowRightIcon color="var(--color-texts-high-contrast)" width="1.2rem" height="1.2rem" />
          {end}
        </span>
      </div>

      <span className={styles['caption']}>{formatDuration(durationOf(span))}</span>
    </div>
  );
};
